import { LockClosedIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { BsWhatsapp } from "react-icons/bs";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import SendPinModal from "@/app/components/routes/modals/SendPinWhatsappModal";
import { useVerifyUserProfilePinAPI } from "@/app/hooks/api_hooks/PWL/useVerifyUserProfilePinAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useConfiguration } from "@/app/hooks/useConfiguration";

const EMPTY_PIN = ["", "", "", ""];
const DEV_DEFAULT_PIN = "1234";

export interface ICartProfilePinModalProps {
  open: boolean;
  setOpen: (e: boolean) => void;
  userId: string;
  clinicId: string;
  bookingId: string;
  userMobileNumber?: string;
  petId?: string;
  source: string;
  onVerified: () => void;
}

const CartProfilePinModal: React.FC<ICartProfilePinModalProps> = ({
  open,
  setOpen,
  userId,
  clinicId,
  bookingId,
  userMobileNumber,
  petId,
  source,
  onVerified,
}) => {
  const { execute: verifyUserProfilePin } = useVerifyUserProfilePinAPI();
  const { execute: clickLogAPI } = useClickLogAPI();
  const currentUserId = useAppSelector((state) => state?.auth?.userLoginInfo?.user_id);
  const currentUserEmail = useAppSelector((state) => state?.auth?.user?.email);
  const [pinDigits, setPinDigits] = useState(EMPTY_PIN);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendPinModalOpen, setIsSendPinModalOpen] = useState(false);
  const pinInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const configuration = useConfiguration();
  const isDevMode = configuration.env.mode === "development";

  const isPinComplete = pinDigits.every((digit) => /^\d$/.test(digit));

  const logPinModalClick = (event_type: string, cta_text: string, extra: Partial<IClickLogBody> = {}) => {
    clickLogAPI({
      event_type,
      cta_text,
      scr_id: "CART_PIN_CHECK",
      source,
      timestamp: Date.now(),
      user_id: currentUserId,
      vms_user_id: currentUserEmail,
      client_id: userId,
      pet_id: petId,
      clinic_id: clinicId,
      booking_id: bookingId,
      ...extra,
    });
  };

  const handleClose = () => {
    logPinModalClick("CART_PIN_MODAL_CLOSED", "Close");
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      logPinModalClick("CART_PIN_MODAL_SHOWN", "Verify Clinic Visit PIN");
      setTimeout(() => pinInputRefs.current[0]?.focus(), 100);
      return;
    }
    setPinDigits(EMPTY_PIN);
    setIsVerifying(false);
    setIsSendPinModalOpen(false);
  }, [open]);

  const handlePinChange = (index: number, value: string) => {
    const sanitizedValue = value.replace(/\D/g, "").slice(-1);
    setPinDigits((prev) => {
      const updatedDigits = [...prev];
      updatedDigits[index] = sanitizedValue;
      return updatedDigits;
    });
    if (sanitizedValue && index < 3) {
      pinInputRefs.current[index + 1]?.focus();
    }
  };

  const handlePinKeyDown = (index: number, key: string) => {
    if (key === "Enter") {
      handleVerifyPin();
      return;
    }
    if (key !== "Backspace") return;
    if (pinDigits[index]) {
      setPinDigits((prev) => {
        const updatedDigits = [...prev];
        updatedDigits[index] = "";
        return updatedDigits;
      });
      return;
    }
    if (index > 0) {
      setPinDigits((prev) => {
        const updatedDigits = [...prev];
        updatedDigits[index - 1] = "";
        return updatedDigits;
      });
      pinInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePinPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedDigits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!pastedDigits) return;
    event.preventDefault();
    setPinDigits(EMPTY_PIN.map((_, index) => pastedDigits[index] ?? ""));
    pinInputRefs.current[Math.min(pastedDigits.length, 3)]?.focus();
  };

  const handleVerifyPin = async () => {
    if (!isPinComplete || isVerifying) return;
    if (!userId || !clinicId || !bookingId) {
      toast.error("Required booking details are missing for PIN verification");
      return;
    }
    logPinModalClick("CART_PIN_VERIFY_CLICKED", "Verify & Continue");
    if (isDevMode && pinDigits.join("") === DEV_DEFAULT_PIN) {
      toast.success("PIN verified successfully");
      setOpen(false);
      onVerified();
      return;
    }
    setIsVerifying(true);
    const response = await verifyUserProfilePin({
      pin: pinDigits.join(""),
      user_id: userId,
      clinic_id: clinicId,
      booking_id: bookingId,
      intent: "checkin",
      source: "VMS",
      created_by: "CM",
    });
    setIsVerifying(false);

    logPinModalClick("CART_PIN_VERIFY_RESULT", "Verify & Continue", {
      event_action: response?.status ? "success" : "failed",
    });
    if (response?.status) {
      toast.success(response?.message ?? "PIN verified successfully");
      setOpen(false);
      onVerified();
      return;
    }
    setPinDigits(EMPTY_PIN);
    pinInputRefs.current[0]?.focus();
    // toast.error(response?.message ?? "PIN verification failed");
  };

  const handleSendPin = () => {
    logPinModalClick("CART_PIN_SEND_WHATSAPP_CLICKED", "Send via WhatsApp");
    if (!userId || !userMobileNumber) {
      toast.error("Customer phone number is missing");
      return;
    }
    setIsSendPinModalOpen(true);
  };

  if (!open) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
        <div className="relative w-full max-w-[420px] rounded-2xl bg-white px-6 pb-6 pt-8 shadow-2xl">
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full text-[#6D717F] transition-colors hover:bg-[#F2F4F7] hover:text-[#111111]"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <div className="flex flex-col items-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F2F4F7]">
              <LockClosedIcon className="h-6 w-6 text-[#111111]" />
            </div>
            <h3 className="mt-4 text-[18px] font-semibold text-[#111111]">Verify Clinic Visit PIN</h3>
            <p className="mt-1 text-[13px] text-[#6D717F]">Enter the customer's 4-digit PIN to open the cart</p>
            {isDevMode && (
              <button
                type="button"
                onClick={() => setPinDigits(DEV_DEFAULT_PIN.split(""))}
                className="mt-3 rounded-lg border border-dashed border-[#F79009] bg-[#FFFAEB] px-3 py-1.5 text-[12px] font-medium text-[#B54708] hover:bg-[#FEF0C7]"
              >
                Dev default PIN: <span className="font-semibold tracking-[0.2em]">{DEV_DEFAULT_PIN}</span> (tap to fill)
              </button>
            )}
          </div>
          <div className="mt-6 flex justify-center gap-3">
            {pinDigits.map((digit, index) => (
              <input
                key={`cart-profile-pin-${index}`}
                ref={(element) => {
                  pinInputRefs.current[index] = element;
                }}
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                disabled={isVerifying}
                onChange={(event) => handlePinChange(index, event.target.value)}
                onKeyDown={(event) => handlePinKeyDown(index, event.key)}
                onPaste={handlePinPaste}
                onFocus={(event) => event.target.select()}
                className={`h-14 w-14 rounded-xl border-2 bg-white text-center text-[22px] font-semibold text-[#111111] outline-none transition-colors focus:border-[#111111] disabled:bg-[#F9FAFB] ${
                  digit ? "border-[#111111]" : "border-[#D9DEE7]"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={handleVerifyPin}
            disabled={!isPinComplete || isVerifying}
            className={`mt-6 h-11 w-full rounded-xl text-[14px] font-semibold transition-all ${
              isPinComplete && !isVerifying
                ? "bg-[#111111] text-white hover:opacity-90"
                : "cursor-not-allowed bg-[#E4E7EC] text-[#98A2B3]"
            }`}
          >
            {isVerifying ? "Verifying..." : "Verify & Continue"}
          </button>
          <div className="mt-4 flex items-center justify-center gap-1 text-[12px] text-[#6D717F]">
            <span>Customer doesn't have the App?</span>
            <button
              type="button"
              onClick={handleSendPin}
              className="flex items-center gap-1 font-semibold text-[#128C7E] hover:underline"
            >
              Send via WhatsApp
              <BsWhatsapp className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
      <SendPinModal
        open={isSendPinModalOpen}
        setOpen={setIsSendPinModalOpen}
        userId={userId}
        userMobileNumber={userMobileNumber ?? ""}
        bookingId={bookingId}
        clinicId={clinicId}
        intent="checkout"
      />
    </>,
    document.body,
  );
};

export default CartProfilePinModal;
